
import React, { lazy, Suspense } from "react";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { Routes, Route } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { AuthProvider } from "./contexts/AuthContext";
import { BookingProvider } from "./contexts/BookingContext";
import { NotificationProvider } from "./contexts/NotificationContext";
import AuthGuard from "./components/guards/AuthGuard";
import MainLayout from "./components/layout/MainLayout";
import CustomerPortalLayout from "./components/layout/CustomerPortalLayout";
import TechnicianLayout from "./components/layout/TechnicianLayout";
import Index from "./pages/Index";
import MaintenancePage from "./pages/MaintenancePage";
import PreviewAccess from "./pages/PreviewAccess";
import TestingDashboard from "./pages/TestingDashboard";
import SmokeTestPage from "./pages/SmokeTestPage";
import LoginPage from "./pages/auth/LoginPage";
import RegisterPage from "./pages/auth/RegisterPage";
import ForgotPasswordPage from "./pages/auth/ForgotPasswordPage";
import ResetPasswordPage from "./pages/auth/ResetPasswordPage";
import EmailConfirmPage from "./pages/auth/EmailConfirmPage";
import AuthCallbackPage from "./pages/auth/AuthCallbackPage";
import VerifyPage from "./pages/auth/VerifyPage";

const LazyEmailConfigPage = lazy(() => import("./pages/auth/EmailConfigPage"));
import BookingPage from "./pages/BookingPage";
import CustomerDashboard from "./pages/CustomerDashboard";
import SchedulePage from "./pages/SchedulePage";
import ProfilePage from "./pages/ProfilePage";
import NotFound from "./pages/NotFound";
import TechnicianDashboard from "./pages/technician/TechnicianDashboard";
import TodayVisitsPage from "./pages/technician/TodayVisitsPage";
import TechnicianCustomersPage from "./pages/technician/TechnicianCustomersPage";
import VisitReportPage from "./pages/technician/VisitReportPage";
import TechnicianReportsPage from "./pages/technician/TechnicianReportsPage";
import TechnicianSchedulePage from "./pages/technician/TechnicianSchedulePage";
import TechnicianSettingsPage from "./pages/technician/TechnicianSettingsPage";
import AdminPanel from "./pages/admin/AdminPanel";
import TechnicianManagement from "./pages/admin/TechnicianManagement";
import ZoneManagement from "./pages/admin/ZoneManagement";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import TermsOfService from "./pages/TermsOfService";
import AboutPage from "./pages/AboutPage";
import BlogPage from "./pages/BlogPage";
import PricingPage from "./pages/PricingPage";
import CareersPage from "./pages/CareersPage";
import FAQPage from "./pages/FAQPage";
import TestimonialsPage from "./pages/TestimonialsPage";
import ContactPage from "./pages/ContactPage";
import PressPage from "./pages/PressPage";
import PressReleasePage from "./pages/PressReleasePage";
import LocationsPage from "./pages/LocationsPage";
import GiftCardsPage from "./pages/GiftCardsPage";
import HelpPage from "./pages/HelpPage";
import ServiceHistory from "./pages/customer/ServiceHistory";
import PaymentMethods from "./pages/customer/PaymentMethods";
import Preferences from "./pages/customer/Preferences";
import Analytics from "./pages/customer/Analytics";
import ShopPage from "./pages/ShopPage";
import MerchPage from "./pages/MerchPage";
import WhyUsPage from "./pages/WhyUsPage";
import ServicesPage from "./pages/ServicesPage";
import PlantParentOnboardingPage from "./pages/onboarding/PlantParentOnboardingPage";
import TechnicianOnboardingPage from "./pages/onboarding/TechnicianOnboardingPage";
import ServiceTrackingPage from "./pages/tracking/ServiceTrackingPage";
import BookingSuccessPage from "./pages/booking-success";
import TrustAndSafetyPage from "./pages/TrustAndSafetyPage";

import ContractGeneratorPage from "./pages/ContractGeneratorPage";
import ContractQuotePage from "./pages/ContractQuotePage";
import SecureAdminAccess from "./components/guards/SecureAdminAccess";
import { ToastProvider } from "./components/ui/toast-provider";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
      staleTime: 5 * 60 * 1000, // 5 minutes
    },
  },
});

const helmetContext = {};

const App = () => {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <NotificationProvider>
          <BookingProvider>
            <HelmetProvider context={helmetContext}>
              <TooltipProvider>
                <ToastProvider>
                <Routes>
                  {/* Homepage - Only accessible page (no header/footer) */}
                  <Route path="/" element={<Index />} />
                  
                  {/* Redirect all other routes to homepage */}
                  <Route path="*" element={<Index />} />
                </Routes>
                </ToastProvider>
              </TooltipProvider>
            </HelmetProvider>
          </BookingProvider>
        </NotificationProvider>
      </AuthProvider>
    </QueryClientProvider>
  );
};

export default App;
